import type { Project, ScenarioId } from '@finsim/model';
import type { ReportPageSize } from '../../../entities/report';
import type { ValueMode } from '../../../entities/view-settings';
import type { Locale } from '../../../shared/lib/locale';

/** Sections the user can include or leave out, in report order. */
export const REPORT_SECTIONS = [
  'summary',
  'assumptions',
  'variants',
  'comparison',
  'risk',
  'appendix',
] as const;
export type ReportSectionKey = (typeof REPORT_SECTIONS)[number];

export interface ReportOptions {
  locale: Locale;
  pageSize: ReportPageSize;
  valueMode: ValueMode;
  /** Variants to include; the report keeps the project's order. */
  scenarioIds: readonly ScenarioId[];
  sections: Readonly<Record<ReportSectionKey, boolean>>;
}

/** Letter in the Americas' locales would go here; the app's locales (en-GB, bg-BG) use A4. */
export const DEFAULT_PAGE_SIZE: ReportPageSize = 'A4';

/** Everything included, for the variants shown in charts. */
export function defaultReportOptions(
  project: Project,
  hidden: ReadonlySet<string>,
  locale: Locale,
  valueMode: ValueMode,
): ReportOptions {
  const visible = project.scenarios.filter((s) => !hidden.has(s.id)).map((s) => s.id);
  return {
    locale,
    pageSize: DEFAULT_PAGE_SIZE,
    valueMode,
    scenarioIds: visible.length > 0 ? visible : project.scenarios.map((s) => s.id),
    sections: Object.fromEntries(REPORT_SECTIONS.map((k) => [k, true])) as Record<
      ReportSectionKey,
      boolean
    >,
  };
}
